import {
  CellClassParams,
  CellClickedEvent,
  CellStyle,
  ColDef,
  ColGroupDef,
  ValueGetterParams,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import { ProjectBasic } from '../../../../lib/functions/project'
import { RoleBasic } from '../../../../lib/functions/role'
import {
  ClientSideSelectFilter,
  ClientSideTextFilter,
} from '../../../containers/BulkSheetView/components/filter'
import { MemberProjectSelectFilter } from '../components/filters/MemberProjectSelectFilter'
import { FixedSelectFilter } from '../components/filters/FixedSelectFilter'
import { ColumnType } from '../../../containers/commons/AgGrid'
import IconCellRenderer from '../../../containers/commons/AgGrid/components/cell/common/iconCell'
import { MemberWorkloadStatusCellRenderer } from '../components/CellRenderer/MemberWorkloadStatusCellRenderer'
import {
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
  displayNameComparator,
  EstimatedActualType,
  TimeGrain,
  AssignmentType,
} from '../MemberWorkloadStatus'
import {
  ROW_NUMBER_COL_DEF,
  INFORMATION_COLUMN_GROUP_DEF_BASE,
  generateTotalColumnDef,
  WORKMONTHS_FIELD_PREFIX,
  CHANGE_LOG_COLUMN_GROUP,
  generateWorkMonthColDef,
  WorkloadUnit,
  WorkingDayCalendar,
} from './commonColumnDef'
import { projectCellValueFormatter } from '.'
import {
  calculateAlertLimitFromCalendar,
  applyCellAlertStyle,
  applyCellAlertStyleForResourcePlan,
  applyCellAlertStyleForEstimated,
  applyCellAlertStyleForActual,
} from './alertLogic'
import DateVO from '../../../../vo/DateVO'
import { SearchFilter } from '../../WbsItemSearch/WbsItemSearchToolBar/WbsItemSearchConditions/WbsItemSearchCondition'
import { openWbsItemSearch } from '../../WbsItemSearch/wbsItemSearchOptions'
import { Color } from '../../../../styles/commonStyles'
import {
  AggregateTarget,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import taskActualWorkApi from '../../../../lib/functions/taskActualWork'

const isEstimatedActualRow = (row?: MemberWorkloadStatusRow) => {
  return (
    row?.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL ||
    row?.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL
  )
}
const isTotalRow = (row?: MemberWorkloadStatusRow) => {
  return row?.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL
}

const getWorkMonthValue = (
  row: MemberWorkloadStatusRow,
  yearMonth: string
): number => {
  return (
    row.body.memberWorkMonths?.find(m => m.yearMonth === yearMonth)?.manMonth ??
    0
  )
}

const isProjectParentRow = (row?: MemberWorkloadStatusRow): boolean => {
  return (
    row?.type === MemberWorkloadStatusType.PROJECT ||
    (row?.isParentRow === true &&
      row?.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL)
  )
}

export const getEstimatedActualTypeLabel = (
  estimatedActualType?: EstimatedActualType
): string => {
  if (
    !estimatedActualType ||
    estimatedActualType === EstimatedActualType.DEFAULT
  ) {
    return ''
  }
  switch (estimatedActualType) {
    case EstimatedActualType.RESOURCE_PLAN:
      return intl.formatMessage({ id: 'memberWorkload.status.resourcePlan' })
    case EstimatedActualType.ESTIMATED:
      return intl.formatMessage({ id: 'memberWorkload.status.estimated' })
    case EstimatedActualType.ACTUAL:
      return intl.formatMessage({ id: 'memberWorkload.status.actual' })
    default:
      return ''
  }
}

const getMemberParamsCellStyles = (
  params: CellClassParams<MemberWorkloadStatusRow>
) => {
  const row: MemberWorkloadStatusRow | undefined = params.data
  if (row?.isRootParentRow) {
    return { color: 'inherit' }
  }
  if (row?.type !== MemberWorkloadStatusType.MEMBER) {
    return { color: 'transparent' }
  }
  return { color: 'inherit' }
}

export const AUTO_GROUP_COLUMN_DEF_GROUP_ROW_BY_MEMBER = {
  field: 'body.user',
  headerName: intl.formatMessage({ id: 'memberWorkload.status.user' }),
  width: 150,
  hide: false,
  pinned: true,
  suppressMovable: true,
  cellRendererParams: params => {
    const row: MemberWorkloadStatusRow | undefined = params.data

    const iconShow =
      row?.isRootParentRow || row?.type === MemberWorkloadStatusType.MEMBER

    return {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      innerRenderer: IconCellRenderer,
      labelField: 'body.user.name',
      iconUrlField: 'body.user.iconUrl',
      iconShow: iconShow,
    }
  },
  cellClassRules: {
    'hide-auto-group-expander': (
      params: CellClassParams<MemberWorkloadStatusRow>
    ) => isProjectParentRow(params.data),
  },
  editable: false,
  valueGetter: (params: ValueGetterParams) => {
    const row: MemberWorkloadStatusRow = params.data
    return row.body.user?.name || ''
  },
  filter: ClientSideTextFilter,
  floatingFilter: true,
  cellStyle: getMemberParamsCellStyles,
}

const getMemberUuid = (row: MemberWorkloadStatusRow) => {
  const rowBody = row?.body
  return rowBody?.user?.uuid
}

export const COLUMN_DEF_GROUP_ROW_BY_MEMBER:
  | (ColDef<MemberWorkloadStatusRow> | ColGroupDef<MemberWorkloadStatusRow>)[]
  | null = [
  ROW_NUMBER_COL_DEF,
  {
    ...INFORMATION_COLUMN_GROUP_DEF_BASE,
    children: [
      {
        field: 'body.user.code',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.user.code',
        }),
        width: 120,
        hide: true,
        lockPosition: true,
        pinned: true,
        floatingFilter: true,
        filter: 'clientSideTextFilter',
        cellStyle: getMemberParamsCellStyles,
      },
      {
        field: 'body.division',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.user.division',
        }),
        width: 120,
        lockPosition: true,
        pinned: true,
        type: [ColumnType.autocomplete],
        cellRendererParams: {
          uiMeta: { tree: true },
        },
        floatingFilter: true,
        filter: ClientSideSelectFilter,
        comparator: displayNameComparator,
        cellStyle: getMemberParamsCellStyles,
      },
      {
        field: 'body.position',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.user.position',
        }),
        width: 120,
        lockPosition: true,
        pinned: true,
        type: [ColumnType.autocomplete],
        cellRendererParams: {
          uiMeta: { tree: true },
        },
        floatingFilter: true,
        filter: ClientSideSelectFilter,
        comparator: displayNameComparator,
        cellStyle: getMemberParamsCellStyles,
      },
      {
        field: 'body.user.name',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.user',
        }),
        width: 120,
        hide: true,
        pinned: true,
        sortable: false,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        cellRendererParams: {
          uiMeta: { tree: true },
        },
        valueGetter: params => {
          const row: MemberWorkloadStatusRow | undefined = params.data
          return row?.body?.user?.name || ''
        },
      },
      {
        field: 'body.project',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.project',
        }),
        width: 150,
        pinned: true,
        suppressMovable: true,
        cellRendererSelector: params => {
          const row: MemberWorkloadStatusRow | undefined = params.data
          if (isProjectParentRow(row)) {
            return {
              component: 'agGroupCellRenderer',
              params: {
                suppressCount: true,
                suppressDoubleClickExpand: true,
                innerRenderer: MemberWorkloadStatusCellRenderer,
                innerRendererParams: {
                  uiMeta: {},
                  hideOpneInNewButton: params => !params.data?.isParentRow,
                  hideIcon: params => !params.data?.isParentRow,
                },
              },
            }
          }
          return {
            component: MemberWorkloadStatusCellRenderer,
            params: {
              uiMeta: {},
              hideOpneInNewButton: params => !params.data?.isParentRow,
              hideIcon: params => !params.data?.isParentRow,
            },
          }
        },
        cellStyle: (params: CellClassParams<MemberWorkloadStatusRow>) => {
          const row: MemberWorkloadStatusRow | undefined = params.data
          if (row?.isParentRow) {
            return { color: 'inherit' }
          }
          return isEstimatedActualRow(params.data)
            ? { color: 'transparent' }
            : { color: 'inherit' }
        },
        floatingFilter: true,
        filter: MemberProjectSelectFilter,
        comparator: (
          valueA: string | ProjectBasic,
          valueB: string | ProjectBasic,
          nodeA,
          nodeB
        ): number => {
          const isTotalEstimatedActualRowA =
            nodeA?.data?.type ===
            MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL
          const isTotalEstimatedActualRowB =
            nodeB?.data?.type ===
            MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL

          if (isTotalEstimatedActualRowA && !isTotalEstimatedActualRowB) {
            return 0
          }
          if (!isTotalEstimatedActualRowA && isTotalEstimatedActualRowB) {
            return 0
          }

          const strA: string = projectCellValueFormatter(valueA)
          const strB: string = projectCellValueFormatter(valueB)
          return strA.localeCompare(strB)
        },
        filterParams: {
          getValue: value => projectCellValueFormatter(value),
          getLabel: value => projectCellValueFormatter(value),
          valueGetter: ({ node }) => {
            const row = node?.data as MemberWorkloadStatusRow | undefined
            const project = row?.body?.project as
              | ProjectBasic
              | string
              | undefined
            if (!project) return undefined
            const label = projectCellValueFormatter(project)
            const code = typeof project === 'object' ? project?.code : undefined
            const totalLabel = intl.formatMessage({
              id: 'project.resourcePlan.total',
            })
            if (label === totalLabel && code === '') return undefined
            return project
          },
          valueFormatter: ({ value }) => projectCellValueFormatter(value),
        },
      },
      {
        field: 'body.role',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.role',
        }),
        hide: true,
        type: [ColumnType.autocomplete],
        pinned: true,
        cellRendererParams: {
          uiMeta: {},
        },
        floatingFilter: true,
        filter: ClientSideSelectFilter,
        comparator: (
          valueA: string | RoleBasic,
          valueB: string | RoleBasic
        ): number => {
          const strA = typeof valueA === 'string' ? valueA : valueA.name
          const strB = typeof valueB === 'string' ? valueB : valueB.name
          return strA.localeCompare(strB)
        },
        cellStyle: getMemberParamsCellStyles,
      },
      {
        field: 'body.validFrom',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.validFrom',
        }),
        hide: true,
        pinned: true,
        type: [ColumnType.dateTime],
        floatingFilter: true,
        cellClass: 'dateTimeStyle',
        cellStyle: getMemberParamsCellStyles,
      },
      {
        field: 'body.validTo',
        headerName: intl.formatMessage({
          id: 'memberWorkload.status.validTo',
        }),
        hide: true,
        pinned: true,
        type: [ColumnType.dateTime],
        floatingFilter: true,
        cellClass: 'dateTimeStyle',
        cellStyle: getMemberParamsCellStyles,
      },
    ],
  },
  {
    field: 'body.type',
    headerName: intl.formatMessage({ id: 'memberWorkload.status.type' }),
    pinned: true,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    sortable: false,
    floatingFilter: true,
    filter: FixedSelectFilter,
    filterParams: {
      getValue: (value: string) => value,
      getLabel: (value: string) => value,
      values: [
        intl.formatMessage({ id: 'memberWorkload.status.resourcePlan' }),
        intl.formatMessage({ id: 'memberWorkload.status.estimated' }),
        intl.formatMessage({ id: 'memberWorkload.status.actual' }),
      ],
      valueGetter: ({ node }) => {
        return getEstimatedActualTypeLabel(node?.data?.estimatedActualType)
      },
    },
    valueGetter: params => {
      return getEstimatedActualTypeLabel(params.data?.estimatedActualType)
    },
  },
  generateTotalColumnDef(MemberWorkloadStatusType.PROJECT, getMemberUuid),
  {
    groupId: WORKMONTHS_FIELD_PREFIX,
    headerName: '',
  },
  CHANGE_LOG_COLUMN_GROUP,
]

export const generateWorkMontColDefGroupRowByMember = (
  yearMonth: string,
  getWorkingTimeTotal: (yearMonth: string) => string
) => {
  const columnDef: ColDef = generateWorkMonthColDef(
    yearMonth,
    getWorkingTimeTotal,
    MemberWorkloadStatusType.PROJECT,
    getMemberUuid
  )
  return {
    ...columnDef,
    cellStyle: (params: CellClassParams) => {
      const workingDayCalendars: WorkingDayCalendar | undefined =
        params.context?.workingDayCalendars
      const workloadUnit: WorkloadUnit | undefined =
        params.context?.workloadUnit

      const currentYearMonth = params.colDef.colId || params.colDef.field

      const alertThreshold = params.context?.alertThreshold
      const row = params.data as MemberWorkloadStatusRow | undefined

      if (!currentYearMonth || !workingDayCalendars || !workloadUnit) {
        const baseCellStyle =
          typeof columnDef.cellStyle === 'function'
            ? columnDef.cellStyle(params)
            : columnDef.cellStyle || {}
        return baseCellStyle
      }

      const baseStyle =
        typeof columnDef.cellStyle === 'function'
          ? columnDef.cellStyle(params)
          : columnDef.cellStyle || {}

      const timeGrain = params.context?.timeGrain

      const alertUpperLimit = calculateAlertLimitFromCalendar(
        workingDayCalendars,
        currentYearMonth,
        workloadUnit,
        timeGrain
      )

      if (
        row?.estimatedActualType === EstimatedActualType.RESOURCE_PLAN &&
        alertThreshold
      ) {
        return applyCellAlertStyleForResourcePlan(
          params.value,
          alertUpperLimit,
          baseStyle || {},
          alertThreshold
        )
      }

      if (
        row?.estimatedActualType === EstimatedActualType.ESTIMATED &&
        alertThreshold
      ) {
        const parentNode = params.node?.parent
        const parentRow = parentNode?.data as
          | MemberWorkloadStatusRow
          | undefined
        if (
          parentRow?.estimatedActualType === EstimatedActualType.RESOURCE_PLAN
        ) {
          const isTotal = isTotalRow(row)
          const currentValue = isTotal
            ? params.value
            : getWorkMonthValue(row, currentYearMonth)
          const resourcePlanValue =
            isTotal && parentNode
              ? params.api?.getValue(currentYearMonth, parentNode)
              : getWorkMonthValue(parentRow, currentYearMonth)
          const alertStyle = applyCellAlertStyleForEstimated(
            currentValue,
            resourcePlanValue,
            baseStyle || {},
            alertThreshold
          )
          const clickableStyle = getWorkMonthCellStyle(() =>
            workMonthColumnClickable(params.data, currentYearMonth)
          )
          return { ...alertStyle, ...clickableStyle }
        }
      }

      if (
        row?.estimatedActualType === EstimatedActualType.ACTUAL &&
        alertThreshold
      ) {
        const siblings = params.node?.parent?.childrenAfterGroup
        const estimatedNode = siblings?.find(
          sibling =>
            (sibling.data as MemberWorkloadStatusRow)?.estimatedActualType ===
            EstimatedActualType.ESTIMATED
        )
        const estimatedRow = estimatedNode?.data as
          | MemberWorkloadStatusRow
          | undefined
        if (estimatedRow && estimatedNode) {
          const isTotal = isTotalRow(row)
          const currentValue = isTotal
            ? params.value
            : getWorkMonthValue(row, currentYearMonth)
          const estimatedValue = isTotal
            ? params.api?.getValue(currentYearMonth, estimatedNode)
            : getWorkMonthValue(estimatedRow, currentYearMonth)
          const alertStyle = applyCellAlertStyleForActual(
            currentValue,
            estimatedValue,
            baseStyle || {},
            alertThreshold
          )
          const clickableStyle = getWorkMonthCellStyle(() =>
            workMonthColumnClickable(params.data, currentYearMonth)
          )
          return { ...alertStyle, ...clickableStyle }
        }
      }

      const alertStyle = applyCellAlertStyle(
        params.value,
        alertUpperLimit,
        baseStyle || {}
      )

      const clickableStyle = getWorkMonthCellStyle(() =>
        workMonthColumnClickable(params.data, currentYearMonth)
      )

      return {
        ...alertStyle,
        ...clickableStyle,
      }
    },
    onCellClicked: onWorkMonthCellClicked,
  }
}

const workMonthColumnClickable = (
  row: MemberWorkloadStatusRow,
  yearMonth: string | undefined
): boolean => {
  if (!row || !yearMonth) return false

  const isEstimatedActualRow =
    row?.type === MemberWorkloadStatusType.ESTIMATED_ACTUAL

  const hasData =
    row.body?.memberWorkMonths?.some(
      workMonth =>
        workMonth.yearMonth === yearMonth && (workMonth.manMonth ?? 0) > 0
    ) ?? false

  return isEstimatedActualRow && hasData
}

const calculateDateRange = (
  yearMonth: string | undefined,
  timeGrain: TimeGrain
): { startDate: DateVO; endDate: DateVO } => {
  const date = new DateVO(yearMonth)

  if (timeGrain === TimeGrain.MONTH) {
    return {
      startDate: date,
      endDate: date.addMonths(1).addDays(-1),
    }
  } else {
    return {
      startDate: date,
      endDate: date.addDays(6),
    }
  }
}

const onWorkMonthCellClicked = async (event: CellClickedEvent) => {
  if (!(event.event instanceof PointerEvent)) {
    return
  }

  const row: MemberWorkloadStatusRow = event.data
  const yearMonth = event.colDef.colId || event.colDef.field
  const assignmentType = event.context?.assignmentType as AssignmentType
  const timeGrain = event.context?.timeGrain as TimeGrain
  const aggregateTarget = event.context?.aggregateTarget as AggregateTarget

  if (!workMonthColumnClickable(row, yearMonth)) {
    return
  }

  const dateRange = calculateDateRange(yearMonth, timeGrain)

  if (row.estimatedActualType === EstimatedActualType.ESTIMATED) {
    const searchFilter: Partial<SearchFilter> = {}

    if (assignmentType === AssignmentType.RESPONSIBLE) {
      searchFilter.responsibleUuid = row.body.user!.uuid
    } else {
      searchFilter.assigneeUuid = row.body.user!.uuid
    }

    searchFilter.scheduledEndDate = {
      from: dateRange.startDate,
      to: dateRange.endDate,
    }

    if (aggregateTarget === WbsItemType.TASK) {
      searchFilter.types = [WbsItemType.TASK]
    } else {
      searchFilter.types = [WbsItemType.DELIVERABLE]
    }

    openWbsItemSearch(row.body.project!.uuid, searchFilter)
  }

  if (row.estimatedActualType === EstimatedActualType.ACTUAL) {
    const response = await taskActualWorkApi.getTaskCodes({
      projectUuid: row.body.project!.uuid,
      userUuid: row.body.user!.uuid,
      startDate: dateRange.startDate.formatForApi()!,
      endDate: dateRange.endDate.formatForApi()!,
    })

    if (response.json && response.json.length > 0) {
      const taskCodes = response.json.join(',')
      const searchFilter: Partial<SearchFilter> = {
        code: taskCodes,
      }
      openWbsItemSearch(row.body.project!.uuid, searchFilter)
    }
  }
}

const getWorkMonthCellStyle = (clickable: () => boolean): CellStyle => {
  const baseStyle: CellStyle = {
    justifyContent: 'flex-end',
    color: 'black',
  }

  if (!clickable()) return baseStyle

  return {
    ...baseStyle,
    cursor: 'pointer',
    color: Color.MAIN,
  }
}
